import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import Reveal from "@/components/Reveal";
import Seo, { LOCAL_BUSINESS_LD, breadcrumbLd } from "@/components/Seo";

const GALLERY = [
  {
    src: "https://images.unsplash.com/photo-1580911498851-4999ad5327b6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzR8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjB3b29kZW4lMjBzdGFpcmNhc2UlMjBpbnRlcmlvcnxlbnwwfHx8fDE3ODMwMjYwODl8MA&ixlib=rb-4.1.0&q=85",
    title: "Oak Staircase — Fulwood",
    tag: "Staircases",
    span: "md:col-span-8 md:row-span-2 aspect-[4/5] md:aspect-auto",
  },
  {
    src: "https://images.unsplash.com/photo-1722349674028-a148f4364e43?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNzl8MHwxfHNlYXJjaHwzfHxmaXR0ZWQlMjBmdXJuaXR1cmUlMjB3YXJkcm9iZSUyMGN1c3RvbXxlbnwwfHx8fDE3ODMwMjYwODl8MA&ixlib=rb-4.1.0&q=85",
    title: "Fitted Wardrobes — Dore",
    tag: "Fitted Furniture",
    span: "md:col-span-4 aspect-[4/5]",
  },
  {
    src: "https://images.unsplash.com/photo-1621506821957-1b50ab7787a4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAxODF8MHwxfHNlYXJjaHwxfHx3b29kZW4lMjBkZWNraW5nJTIwb3V0ZG9vciUyMGxpdmluZ3xlbnwwfHx8fDE3ODMwMjYwODl8MA&ixlib=rb-4.1.0&q=85",
    title: "Hardwood Deck — Ecclesall",
    tag: "Decking",
    span: "md:col-span-4 aspect-[4/3]",
  },
  {
    src: "https://images.unsplash.com/photo-1754063257992-bb9eabdbdd86?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTN8MHwxfHNlYXJjaHwzfHxtb2Rlcm4lMjBob3VzZSUyMGV4dGVuc2lvbiUyMGV4dGVyaW9yfGVufDB8fHx8MTc4MzAyNjA4OXww&ixlib=rb-4.1.0&q=85",
    title: "Rear Extension — Dronfield",
    tag: "Extensions",
    span: "md:col-span-6 aspect-[16/10]",
  },
  {
    src: "https://images.unsplash.com/photo-1497219055242-93359eeed651?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODF8MHwxfHNlYXJjaHwzfHxiZXNwb2tlJTIwY2FycGVudHJ5JTIwd29vZHdvcmtpbmclMjBjcmFmdHNtYW5zaGlwfGVufDB8fHx8MTc4MzAyNjA4OXww&ixlib=rb-4.1.0&q=85",
    title: "Bespoke Chisel Work",
    tag: "Bespoke Joinery",
    span: "md:col-span-6 aspect-[16/10]",
  },
  {
    src: "https://images.unsplash.com/photo-1547609434-b732edfee020?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODF8MHwxfHNlYXJjaHw0fHxiZXNwb2tlJTIwY2FycGVudHJ5JTIwd29vZHdvcmtpbmclMjBjcmFmdHNtYW5zaGlwfGVufDB8fHx8MTc4MzAyNjA4OXww&ixlib=rb-4.1.0&q=85",
    title: "Workshop, Sheffield",
    tag: "Workshop",
    span: "md:col-span-8 aspect-[16/9]",
  },
  {
    src: "https://images.unsplash.com/photo-1660361338517-8c8fbb3ac264?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTN8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBob3VzZSUyMGV4dGVuc2lvbiUyMGV4dGVyaW9yfGVufDB8fHx8MTc4MzAyNjA4OXww&ixlib=rb-4.1.0&q=85",
    title: "Loft Conversion — Chesterfield",
    tag: "Building Work",
    span: "md:col-span-4 aspect-[4/3]",
  },
];

export default function Gallery() {
  return (
    <div data-testid="gallery-page" className="pt-32 md:pt-40 pb-24">
      <Seo
        title="Joinery Projects Gallery in Sheffield | Woodland Joinery & Building"
        description="Photos of recent joinery, staircase, decking, extension and fitted-furniture projects delivered by Woodland Joinery across Sheffield and South Yorkshire."
        path="/gallery"
        jsonLd={[
          LOCAL_BUSINESS_LD,
          breadcrumbLd([
            { name: "Home", path: "/" },
            { name: "Gallery", path: "/gallery" },
          ]),
        ]}
      />
      <section className="max-w-7xl mx-auto px-6 md:px-12 pb-14 md:pb-20">
        <Reveal>
          <p className="text-[11px] tracking-[0.35em] uppercase text-[#4A5550]">
            Portfolio
          </p>
          <h1 className="mt-4 font-display text-5xl md:text-7xl lg:text-8xl text-[#1F3B2D] leading-[0.95] max-w-4xl">
            Our recent <em className="text-[#C8A24B] not-italic font-light">projects.</em>
          </h1>
          <p className="mt-8 max-w-2xl text-lg text-[#4A5550] leading-relaxed">
            A selection of joinery, decking, staircase and building work delivered
            across Sheffield and the surrounding areas.
          </p>
        </Reveal>
      </section>

      <section className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-8 md:auto-rows-[220px]">
          {GALLERY.map((g, i) => (
            <Reveal
              key={i}
              delay={i * 90}
              className={`relative overflow-hidden group border border-[#E5E0D8] hover:border-[#C8A24B] transition ${g.span}`}
            >
              <img
                src={g.src}
                alt={g.title}
                className="w-full h-full object-cover transition-transform duration-[1400ms] ease-out group-hover:scale-105"
                data-testid={`gallery-image-${i}`}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent opacity-90 group-hover:opacity-100 transition" />
              <div className="absolute inset-0 flex flex-col justify-end p-6 md:p-8">
                <span className="text-[10px] tracking-[0.3em] uppercase text-[#C8A24B]">
                  {g.tag}
                </span>
                <div className="mt-2 flex items-end justify-between gap-3">
                  <h3 className="font-display text-xl md:text-2xl text-white">
                    {g.title}
                  </h3>
                  <ArrowUpRight className="h-5 w-5 text-white/80 shrink-0 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
                </div>
              </div>
            </Reveal>
          ))}
        </div>

        <div className="mt-16 md:mt-24 text-center">
          <Reveal>
            <Link to="/contact" data-testid="gallery-cta-quote" className="btn-primary">
              Start Your Project <ArrowUpRight className="h-4 w-4" />
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
